import { ref } from 'vue'
import axios from '@/api/client'
import { useAuthStore } from '@/stores/auth'

// Per-company geo + migration-source context, shared across components.
// migration_source = the previous CRM/app the company migrated from (e.g. HCP);
// its abbreviation is the label of the dispatch "show migrated jobs" toggle.
// country_code drives US (ZIP) vs CA (postal) UI in the territories module.
const context = ref(null)
let inflight = null
let loadedDomainId = null
let loadGeneration = 0

function currentDomainId() {
  try {
    const auth = useAuthStore()
    return auth.domain?.id
      || auth.user?.current_domain_id
      || auth.user?.primary_domain_id
      || null
  } catch {
    return null
  }
}

function applyContextDefaults(data) {
  const ctx = data || {}
  ctx.chat_message_order = ctx.chat_message_order || 'newest_last'
  ctx.sms_message_order = ctx.sms_message_order || 'newest_last'
  if (ctx.chat_jump_to_newest == null) ctx.chat_jump_to_newest = true
  if (ctx.oliver_widget_enabled == null) ctx.oliver_widget_enabled = true
  // NULL = default OFF — Postal/ZIP check on New Job is opt-in (TICK-00037).
  if (ctx.new_job_service_area_check == null) ctx.new_job_service_area_check = false
  // TICK-00040: TV Club module visible by default unless the company turned it off.
  if (ctx.tv_club_enabled == null) ctx.tv_club_enabled = true
  // FSJ-6-00071: Loyalty & Referrals is fail-closed (OFF when unset).
  if (ctx.loyalty_referrals_enabled == null) ctx.loyalty_referrals_enabled = false
  // Dispatch exceptions strip is opt-in (OFF by default) per company.
  if (ctx.dispatch_exceptions_enabled == null) ctx.dispatch_exceptions_enabled = false
  // Property details on the customer card are opt-in (OFF by default).
  if (ctx.customer_property_details_enabled == null) ctx.customer_property_details_enabled = false
  // A supervisor reading any customer's history is opt-in (OFF by default).
  if (ctx.supervisor_customer_history_enabled == null) ctx.supervisor_customer_history_enabled = false
  // Full Task create button on /tasks is visible by default; opt-in to hide.
  if (ctx.tasks_full_task_create_hidden == null) ctx.tasks_full_task_create_hidden = false
  // Cleaning-only reference catalogs are shown by default; opt-in to hide
  // them for non-cleaning companies (TICK-00230 / TICK-00231).
  if (ctx.cleaning_catalogs_hidden == null) ctx.cleaning_catalogs_hidden = false
  // Request Quote button on a job's Parts section is shown by default;
  // opt-in to hide it for companies with no parts department (TICK-00283).
  if (ctx.parts_request_quote_hidden == null) ctx.parts_request_quote_hidden = false
  // Office Job Constructor is opt-in (OFF by default): the office job card
  // and New Job form stay built-in until a company turns the flag on.
  if (ctx.office_job_constructor_enabled == null) ctx.office_job_constructor_enabled = false
  // MD-4-00027: duplicate phone / e-mail blocking is opt-in (OFF by
  // default) — companies keep the non-blocking "possible duplicate"
  // warning until they turn a switch on.
  if (ctx.crm_block_duplicate_phone == null) ctx.crm_block_duplicate_phone = false
  if (ctx.crm_block_duplicate_email == null) ctx.crm_block_duplicate_email = false
  // AP-7-00368/00369: empty CRM pages (Lead Sources, Flow Builder) are shown by
  // default; opt-in to hide them per company so a business that does not use
  // them is not shown a blank screen.
  if (ctx.crm_lead_sources_hidden == null) ctx.crm_lead_sources_hidden = false
  if (ctx.crm_flow_builder_hidden == null) ctx.crm_flow_builder_hidden = false
  // MD-4-00041/00042/00043: 12-hour AM/PM manual entry for the office New Job
  // Start Time is opt-in (OFF by default) — every company keeps the native
  // time picker until it turns this on.
  if (ctx.newjob_time_12h == null) ctx.newjob_time_12h = false
  // AP-7-00340: per-company custom Oliver badge icon URL. NULL = use the
  // built-in default icon (nothing changes for existing companies).
  if (ctx.oliver_badge_icon_url == null) ctx.oliver_badge_icon_url = null
  delete ctx._load_failed
  return ctx
}

export function useCompanyContext() {
  function reset() {
    loadGeneration += 1
    context.value = null
    loadedDomainId = null
    inflight = null
  }

  async function load(force = false) {
    const domainId = currentDomainId()
    if (context.value && !force && loadedDomainId === domainId && domainId != null) {
      return context.value
    }
    if (inflight && !force) return inflight
    const generation = ++loadGeneration
    inflight = axios
      .get('/domains/company-context/')
      .then(({ data }) => {
        if (generation !== loadGeneration) return context.value
        context.value = applyContextDefaults(data || {})
        loadedDomainId = domainId
        return context.value
      })
      .catch(() => {
        if (generation !== loadGeneration) return context.value
        // Fail-closed for Club on API errors — do not assume the module is ON.
        context.value = {
          country_code: 'CA',
          migration_source_abbrev: 'ST',
          migration_source_code: null,
          chat_message_order: 'newest_last',
          sms_message_order: 'newest_last',
          chat_jump_to_newest: true,
          oliver_widget_enabled: true,
          new_job_service_area_check: false,
          tv_club_enabled: false,
          loyalty_referrals_enabled: false,
          dispatch_exceptions_enabled: false,
          customer_property_details_enabled: false,
          supervisor_customer_history_enabled: false,
          tasks_full_task_create_hidden: false,
          cleaning_catalogs_hidden: false,
          parts_request_quote_hidden: false,
          office_job_constructor_enabled: false,
          crm_block_duplicate_phone: false,
          crm_block_duplicate_email: false,
          crm_lead_sources_hidden: false,
          crm_flow_builder_hidden: false,
          newjob_time_12h: false,
          oliver_badge_icon_url: null,
          _load_failed: true,
        }
        loadedDomainId = domainId
        return context.value
      })
      .finally(() => {
        inflight = null
      })
    return inflight
  }

  // Assign already-saved settings onto the shared context so every open view
  // (New Job, dispatch board, Oliver widget) repaints the affected bindings
  // immediately — without a page reload and without refetching the whole
  // context. Company Settings calls this right after a successful save.
  function patch(partial) {
    if (!partial || typeof partial !== 'object') return context.value
    // Nothing cached yet: the next load() fetches the saved values anyway, and
    // seeding a partial object here would hide the fields this payload lacks
    // (country_name, migration_source_abbrev, ...) behind the cache.
    if (!context.value) return null
    // country_name is derived server-side; drop it when the code changes so
    // consumers fall back to the code instead of showing the old country.
    if (partial.country_code && partial.country_code !== context.value.country_code) {
      delete context.value.country_name
    }
    for (const [key, value] of Object.entries(partial)) {
      if (value === undefined) continue
      context.value[key] = value
    }
    delete context.value._load_failed
    return context.value
  }

  return { context, load, patch, reset }
}
